import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { DicomMetadataStore, MODULE_TYPES } from '@ohif/core';

import Dropzone from 'react-dropzone';
import filesToStudies from './filesToStudies';

import { extensionManager } from '../../App.tsx';

import useProgressBar from '../../hooks/useProgressBar';

import { Icon, Button, LoadingIndicatorProgress } from '@ohif/ui';

const getLoadButton = (onDrop, text, isDir) => {
  return (
    <Dropzone onDrop={onDrop} noDrag>
      {({ getRootProps, getInputProps }) => (
        <div
          {...getRootProps()}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <Button
            rounded="full"
            variant="contained" // outlined
            disabled={false}
            endIcon={<Icon name="launch-arrow" />} // launch-arrow | launch-info
            className={classnames('font-medium', 'ml-2')}
            onClick={() => {}}
          >
            {text}
            {isDir ? (
              <input
                {...getInputProps()}
                webkitdirectory="true"
                mozdirectory="true"
              />
            ) : (
              <>
                <input {...getInputProps()} />
              </>
            )}
          </Button>
          {!isDir && (
            <span className="text-blue-300 text-lg text-center">
              Image Dicom ou .zip
            </span>
          )}
        </div>
      )}
    </Dropzone>
  );
};

type LocalProps = {
  modePath: string;
};

function Local({ modePath }: LocalProps) {
  const navigate = useNavigate();
  const dropzoneRef = useRef();
  const { progress, updateProgress } = useProgressBar();
  const [dropInitiated, setDropInitiated] = useState(false);

  // Initializing the dicom local dataSource
  const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE];
  const localDataSources = dataSourceModules.reduce((acc, curr) => {
    const mods = [];
    curr.module.forEach(mod => {
      if (mod.type === 'localApi') {
        mods.push(mod);
      }
    });
    return acc.concat(mods);
  }, []);

  const firstLocalDataSource = localDataSources[0];
  const dataSource = firstLocalDataSource.createDataSource({});

  const microscopyExtensionLoaded = extensionManager.registeredExtensionIds.includes(
    '@ohif/extension-dicom-microscopy'
  );

  const onDrop = async (acceptedFiles, url = null) => {
    setDropInitiated(true);

    updateProgress(0);
    setLabelProgress('Chargement des fichiers...');

    // let studies = await filesToStudies(acceptedFiles,dataSource);
    let studies = await filesToStudies(acceptedFiles, updateProgress);

    console.log('studies ---> ', studies);

    studies = studies.filter(element => element !== undefined);

    const query = new URLSearchParams();

    if (microscopyExtensionLoaded) {
      // TODO: for microscopy, we are forcing microscopy mode, which is not ideal.
      //     we should make the local drag and drop navigate to the worklist and
      //     there user can select microscopy mode
      const smStudies = studies.filter(id => {
        const study = DicomMetadataStore.getStudy(id);
        return (
          study.series.findIndex(
            s => s.Modality === 'SM' || s.instances[0].Modality === 'SM'
          ) >= 0
        );
      });

      if (smStudies.length > 0) {
        smStudies.forEach(id => query.append('StudyInstanceUIDs', id));

        modePath = 'microscopy';
      }
    }

    // Todo: navigate to work list and let user select a mode
    studies
      .filter(id => id !== undefined)
      .forEach(id => query.append('StudyInstanceUIDs', id));
    query.append('datasources', 'dicomlocal');

    navigate(`/${modePath}?${decodeURIComponent(query.toString())}`, {
      state: { OrigineUrl: url },
    });
  };

  // Set body style
  useEffect(() => {
    const urlSearchString = window.location.search;
    const params = new URLSearchParams(urlSearchString);

    if (params?.get('url')) {
      UnzipUrl(params.get('url'));
    }

    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  const [labelProgress, setLabelProgress] = useState('');
  // const [downloadZip, setDownloadZip] = useState(false);

  async function UnzipUrl(url) {
    setDropInitiated(true);
    // setDownloadZip(true);
    const urlToDownload = url;
    try {
      const res = await fetch(urlToDownload);
      if (res.status !== 200) {
        throw new Error('Bad server response');
      }

      const headerName = res.headers.get('content-disposition');
      const contentType = res.headers.get('content-type');
      const contentLength = res.headers.get('content-length');
      updateProgress(0);
      setLabelProgress('Téléchargement en cours...');
      // il est necessaire de clone la reponse car les headers ne peuvent etre lu qu'une seule fois
      // si non cloner cette erreur e lever -> TypeError: Failed to execute 'blob' on 'Response': body stream already read
      const cloneResponse = res.clone();
      const reader = cloneResponse.body.getReader();
      let testProgress = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          updateProgress(100);
          break;
        }
        updateProgress({
          loaded: value.length,
          total: contentLength,
          actualProgress: testProgress,
        });
        testProgress = testProgress + value.length;
      }

      let fileName;

      if (headerName !== null) {
        const regex = /filename="(.*?)"/;
        fileName = regex.exec(headerName)[1];
      } else {
        fileName = urlToDownload.substring(urlToDownload.lastIndexOf('/') + 1);
      }

      // fileName contient plus d'un élément (le premier élément est le texte correspondant à l'expression régulière dans son ensemble,
      // et le deuxième élément est le texte correspondant à la partie capturée entre parenthèses)
      const data = await res.blob();
      contentType !== null ? contentType : data.type;

      if (contentType.includes('zip') || contentType.includes('octet-stream')) {
        const file = new File([data], fileName, { type: contentType });
        // console.log('🚀 ~ file: UnzipUrl.tsx:26 ~ UnzipUrl ~ file:', file);

        onDrop([file], urlToDownload);
      }
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <Dropzone
      ref={dropzoneRef}
      onDrop={acceptedFiles => {
        setDropInitiated(true);
        onDrop(acceptedFiles);
      }}
      noClick
    >
      {({ getRootProps }) => (
        <div {...getRootProps()} style={{ width: '100%', height: '100%' }}>
          <div className="h-screen w-screen flex justify-center items-center ">
            <div
              className="py-8 px-8 mx-auto bg-secondary-dark drop-shadow-md space-y-2 rounded-lg"
              style={{ background: 'linear-gradient(#42a8c1, #00937b)' }}
            >
              <img
                className="block mx-auto h-28"
                src="./logolongBlanc.png"
                alt="OHIF"
              />
              <div className="text-center space-y-2 pt-4">
                {dropInitiated ? (
                  <div className="flex flex-col items-center justify-center pt-48">
                    <LoadingIndicatorProgress
                      className={'w-full h-full bg-black'}
                      progress={progress}
                      textBlock={labelProgress}
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-gray-300 text-base">
                      Note : Vos données ne sont pas téléchargées sur un
                      serveur, elles restent dans l'application locale du
                      navigateur.
                    </p>
                    <p
                      className="text-xg  font-semibold pt-6"
                      style={{ color: '#004ae8' }}
                    >
                      Glissez-déposez les fichiers DICOM ici pour les charger
                      dans le visualiseur.
                    </p>
                    <p className="text-blue-300 text-lg">Ou cliquez sur</p>
                  </div>
                )}
              </div>
              <div className="flex justify-around pt-4 ">
                {getLoadButton(onDrop, 'Charger un fichier', false)}
                {getLoadButton(onDrop, 'Charger un dossier', true)}
              </div>
            </div>
          </div>
        </div>
      )}
    </Dropzone>
  );
}

export default Local;
